import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export interface ProjectSection {
  id: string;
  organization_id: string;
  project_id: string;
  name: string;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectSectionInput {
  organization_id: string;
  project_id: string;
  name: string;
  position?: number;
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/sections`;

export const pmSectionsApiService = {
  async list(params: { organization_id: string; project_id: string }): Promise<{ data: ProjectSection[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({
        organization_id: params.organization_id,
        project_id: params.project_id,
      });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: [], error: result?.error || 'Failed to fetch sections' };
      }

      return { data: (result.data || []) as ProjectSection[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async create(input: ProjectSectionInput): Promise<{ data: ProjectSection | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to create section' };
      }

      return { data: (result.data || null) as ProjectSection | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async update(sectionId: string, organizationId: string, updates: Partial<ProjectSectionInput>): Promise<{ data: ProjectSection | null; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(sectionId)}?${queryParams.toString()}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to update section' };
      }

      return { data: (result.data || null) as ProjectSection | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async delete(sectionId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(sectionId)}?${queryParams.toString()}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to delete section' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },

  async reorder(params: { organization_id: string; project_id: string; section_ids: string[] }): Promise<{ error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to reorder sections' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },
};
